import type { ReactNode } from 'react'
import { Bot, Brain, KeyRound, Newspaper, Puzzle, Repeat, SearchX, Workflow, X, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// Conceitos de empty state de onboarding. Cada um vira um ícone da lib que já
// usamos (lucide) dentro de um tile violeta — sem asset externo, sem pipeline
// de imagem. Pra trocar o ícone de um conceito, mexe só no mapa abaixo.
export type EmptyIllustration =
  | 'agents'
  | 'routines'
  | 'credentials'
  | 'integrations'
  | 'publications'
  | 'memory'
  | 'processes'

const ILLUSTRATION_ICONS: Record<EmptyIllustration, LucideIcon> = {
  agents: Bot,
  routines: Repeat,
  credentials: KeyRound,
  integrations: Puzzle,
  publications: Newspaper,
  memory: Brain,
  processes: Workflow,
}

interface EmptyStateProps {
  illustration?: EmptyIllustration
  title: string
  description?: ReactNode
  // CTA opcional (Button/Link) — o empty vira um convite, não um beco sem saída.
  action?: ReactNode
  className?: string
}

/**
 * Empty state de onboarding (tipo A): ícone num tile violeta + título + texto +
 * CTA, centralizado. Um ícone da lib (lucide) por conceito — sem asset externo.
 */
export function EmptyState({ illustration, title, description, action, className }: EmptyStateProps) {
  const Icon = illustration ? ILLUSTRATION_ICONS[illustration] : null
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      {Icon && (
        <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-brand/10 text-brand dark:bg-brand/15">
          <Icon className="size-7" aria-hidden />
        </div>
      )}
      <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-foreground">{title}</h3>
      {description && (
        <p className="mx-auto mt-1.5 max-w-sm text-[13px] leading-relaxed text-muted-foreground">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

interface FilterEmptyStateProps {
  // Termo de busca atual — se houver, é ecoado no título ("nada bateu com …"),
  // o que dá sensação de resposta em vez de um beco cinza.
  query?: string
  // Ação de limpar os filtros. Presente = vira botão de saída (não é dead-end).
  onClear?: () => void
  clearLabel?: string
  className?: string
}

/**
 * Empty state de busca/filtro sem resultado (tipo B): spot central com ícone,
 * eco do termo buscado e uma saída ("limpar"). Distinto do EmptyState ilustrado
 * (tipo A, onboarding) — aqui é um estado transitório, sem ilustração.
 */
export function FilterEmptyState({ query, onClear, clearLabel = 'Limpar filtros', className }: FilterEmptyStateProps) {
  const q = query?.trim()
  return (
    <div className={cn('flex flex-col items-center gap-2.5 py-12 text-center', className)}>
      <div className="flex size-12 items-center justify-center rounded-2xl bg-brand/10 text-brand dark:bg-brand/15">
        <SearchX className="size-6" aria-hidden />
      </div>
      <p className="text-[15px] font-semibold tracking-[-0.01em] text-foreground">
        {q ? (
          <>
            Nada bateu com{' '}
            <span className="inline-block max-w-full break-all rounded-md bg-brand/10 px-1.5 py-0.5 font-mono text-[13px] text-foreground">{q}</span>
          </>
        ) : (
          'Nada com esses filtros'
        )}
      </p>
      <p className="max-w-xs text-[13px] text-muted-foreground">Tente outro termo ou tire algum filtro.</p>
      {onClear && (
        <Button variant="outline" size="sm" onClick={onClear} className="mt-1">
          <X className="size-3.5" aria-hidden />
          {clearLabel}
        </Button>
      )}
    </div>
  )
}

interface PanelEmptyStateProps {
  // Ícone da lib (lucide) — cada painel escolhe o seu.
  icon: LucideIcon
  title: string
  description?: ReactNode
  action?: ReactNode
  // framed (padrão): card próprio com o mesmo frame da lista cheia — pra seção
  // que vive sob um SectionLabel. framed={false}: sem borda e em escala maior —
  // pra página inteira vazia ou quando já existe um card em volta.
  framed?: boolean
  className?: string
}

/**
 * Empty state de painel (tipo C): tile violeta com ícone + título/subtítulo,
 * na mesma família dos outros empty states. Pra seções/páginas que não podem
 * virar o beco cinza de uma frase solta.
 */
export function PanelEmptyState({ icon: Icon, title, description, action, framed = true, className }: PanelEmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center text-center',
        framed ? 'gap-2 rounded-xl border border-border bg-card px-6 py-8' : 'gap-2.5 px-6 py-12',
        className,
      )}
    >
      <div className={cn('flex items-center justify-center rounded-2xl bg-brand/10 text-brand dark:bg-brand/15', framed ? 'size-12' : 'size-14')}>
        <Icon className={framed ? 'size-6' : 'size-7'} aria-hidden />
      </div>
      <p className={cn('font-semibold tracking-[-0.01em] text-foreground', framed ? 'text-[13px]' : 'text-[15px]')}>{title}</p>
      {description && (
        <p className={cn('leading-relaxed text-muted-foreground', framed ? 'max-w-[36ch] text-[11.5px]' : 'max-w-sm text-[13px]')}>
          {description}
        </p>
      )}
      {action && <div className={framed ? 'mt-1' : 'mt-3'}>{action}</div>}
    </div>
  )
}
